import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, Clock, ArrowLeft, ArrowRight } from "lucide-react";

const styles = [
  { id: "knotless", name: "Knotless Braids", sizes: ["small", "medium", "large"], lengths: ["mid-back", "waist", "butt-length"] },
  { id: "box-braids", name: "Box Braids", sizes: ["small", "medium", "large"], lengths: ["mid-back", "waist", "butt-length"] },
  { id: "fulani", name: "Fulani Braids", sizes: ["small", "medium"], lengths: ["mid-back", "waist", "butt-length"] },
  { id: "boho", name: "Boho Braids", sizes: ["small", "medium"], lengths: ["mid-back", "waist", "butt-length"] },
  { id: "kinky-twist", name: "Kinky Twists", sizes: ["small", "medium"], lengths: ["bob", "mid-back", "waist"] },
  { id: "faux-locs", name: "Faux Locs", sizes: ["any"], lengths: ["mid-back", "waist", "butt-length"] },
  { id: "cornrows", name: "Cornrows", sizes: ["any"], lengths: ["any"] },
  { id: "crochet", name: "Crochet", sizes: ["any"], lengths: ["any"] },
];

const PRICES = {
  "knotless": {"small": {"mid-back": 200, "waist": 220, "butt-length": 240}, "medium": {"mid-back": 180, "waist": 200, "butt-length": 220}, "large": {"mid-back": 160, "waist": 180, "butt-length": 200}},
  "box-braids": {"small": {"mid-back": 200, "waist": 220, "butt-length": 240}, "medium": {"mid-back": 180, "waist": 200, "butt-length": 220}, "large": {"mid-back": 160, "waist": 180, "butt-length": 200}},
  "fulani": {"small": {"mid-back": 140, "waist": 160, "butt-length": 180}, "medium": {"mid-back": 120, "waist": 140, "butt-length": 160}},
  "boho": {"small": {"mid-back": 200, "waist": 220, "butt-length": 240}, "medium": {"mid-back": 200, "waist": 220, "butt-length": 240}},
  "kinky-twist": {"small": {"bob": 200, "mid-back": 220, "waist": 240}, "medium": {"bob": 180, "mid-back": 200, "waist": 220}},
  "faux-locs": {"any": {"mid-back": 170, "waist": 200, "butt-length": 230}},
  "cornrows": {"any": {"any": 50}},
  "crochet": {"any": {"any": 130}},
};

const DURATIONS = {
  "knotless": {"small": "3-4 hrs", "medium": "3-4 hrs", "large": "2-3 hrs"},
  "box-braids": {"small": "4-5 hrs", "medium": "3-5 hrs", "large": "2-3 hrs"},
  "fulani": {"small": "2-3 hrs", "medium": "2-3 hrs"},
  "boho": {"small": "4-5 hrs", "medium": "4-5 hrs"},
  "kinky-twist": {"small": "3-4 hrs", "medium": "3-4 hrs"},
  "faux-locs": {"any": "3-4 hrs"},
  "cornrows": {"any": "1 hr"},
  "crochet": {"any": "2 hrs"},
};

const PricingCalculatorPage = () => {
  const [style, setStyle] = useState("");
  const [size, setSize] = useState("");
  const [length, setLength] = useState("");

  const selectedStyle = styles.find(s => s.id === style);
  const price = style && size && length ? (PRICES[style]?.[size]?.[length] || PRICES[style]?.["any"]?.[length] || PRICES[style]?.["any"]?.["any"] || 0) : 0;
  const onlinePrice = Math.round(price * 0.9);
  const duration = style && size ? (DURATIONS[style]?.[size] || DURATIONS[style]?.["any"] || "") : "";

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="pricing-calculator-page">
      <header className="border-b border-brand-border bg-brand-surface px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-serif text-xl text-brand-text">Afro <span className="text-brand-gold">Magic</span></Link>
        <Link to="/" className="flex items-center gap-1 font-sans text-xs text-brand-text-muted hover:text-brand-gold"><ArrowLeft size={14} /> Back</Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <DollarSign size={28} className="text-brand-gold mx-auto mb-3" />
            <h1 className="font-serif text-3xl text-brand-text">Pricing Calculator</h1>
            <p className="font-sans text-sm text-brand-text-muted mt-2">Get an instant estimate for your braids</p>
          </div>

          <div className="space-y-4">
            {/* Style */}
            <div>
              <label className="font-sans text-xs text-brand-text-muted block mb-2">1. Choose your style</label>
              <div className="grid grid-cols-2 gap-2">
                {styles.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setStyle(s.id); setSize(""); setLength(""); }}
                    data-testid={`calc-style-${s.id}`}
                    className={`px-3 py-2.5 border font-sans text-sm transition-colors ${style === s.id ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-border text-brand-text-muted hover:border-brand-gold/50"}`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            {selectedStyle && selectedStyle.sizes[0] !== "any" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <label className="font-sans text-xs text-brand-text-muted block mb-2">2. Braid size</label>
                <div className="flex gap-2">
                  {selectedStyle.sizes.map(s => (
                    <button
                      key={s}
                      onClick={() => { setSize(s); setLength(""); }}
                      data-testid={`calc-size-${s}`}
                      className={`flex-1 px-3 py-2.5 border font-sans text-sm capitalize transition-colors ${size === s ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-border text-brand-text-muted hover:border-brand-gold/50"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Length */}
            {selectedStyle && (size || selectedStyle.sizes[0] === "any") && selectedStyle.lengths[0] !== "any" && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <label className="font-sans text-xs text-brand-text-muted block mb-2">{selectedStyle.sizes[0] === "any" ? "2" : "3"}. Length</label>
                <div className="flex gap-2">
                  {selectedStyle.lengths.map(l => (
                    <button
                      key={l}
                      onClick={() => { setLength(l); if (selectedStyle.sizes[0] === "any") setSize("any"); }}
                      data-testid={`calc-length-${l}`}
                      className={`flex-1 px-3 py-2.5 border font-sans text-sm capitalize transition-colors ${length === l ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-border text-brand-text-muted hover:border-brand-gold/50"}`}
                    >
                      {l.replace("-", " ")}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Auto-set for cornrows/crochet */}
            {selectedStyle && selectedStyle.sizes[0] === "any" && selectedStyle.lengths[0] === "any" && !size && (() => { setSize("any"); setLength("any"); return null; })()}

            {/* Result */}
            {price > 0 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border-2 border-brand-gold bg-brand-surface p-6 mt-4" data-testid="calc-result">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-sans text-xs text-brand-gold uppercase tracking-wider">Estimated Price</p>
                    <p className="font-serif text-4xl text-brand-text">${price}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-sans text-xs text-brand-gold uppercase tracking-wider">Secure Your Spot</p>
                    <p className="font-serif text-4xl text-brand-gold">$25</p>
                    <p className="font-sans text-[10px] text-brand-text-muted">deposit counts toward total</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 border-t border-brand-border pt-3 mb-4">
                  <span className="flex items-center gap-1 font-sans text-xs text-brand-text-muted"><Clock size={12} className="text-brand-gold" /> Est. {duration}</span>
                  <span className="flex items-center gap-1 font-sans text-xs text-brand-text-muted"><DollarSign size={12} className="text-brand-gold" /> $25 deposit to book</span>
                </div>
                <Link to="/#booking" className="flex items-center justify-center gap-2 w-full py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors" data-testid="calc-book-btn">
                  Book Now — Pay ${onlinePrice} <ArrowRight size={16} />
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculatorPage;
